/**
 * Comments module for Vuex store.
 *
 * Phase 1: one editable comment per user per track, surfaced only in poll mode. In poll
 * voting mode the dialog edits the user's own comment; in poll results mode it shows the
 * anonymous thread (everyone's comments, read-only). The author's identity is never sent
 * from here — the backend derives it from the verified Firebase token and anonymises the
 * thread server-side.
 */
import ApiService from '@/services/ApiService';

export default {
  namespaced: true,

  state: () => ({
    isOpen: false,        // dialog visibility
    mode: 'edit',         // 'edit' (own comment) | 'thread' (anonymous, read-only)
    recordingId: null,    // the track the dialog is about
    myComment: null,      // { body, updated_at } | null — edit mode
    thread: [],           // [{ body, updated_at }] — thread mode
    commentedIds: [],     // recording ids the user has commented on (icon state)
    loading: false,       // fetching myComment / thread
    saving: false,        // persisting an edit
  }),

  getters: {
    isOpen: state => state.isOpen,
    mode: state => state.mode,
    recordingId: state => state.recordingId,
    myComment: state => state.myComment,
    thread: state => state.thread,
    loading: state => state.loading,
    saving: state => state.saving,
    hasComment: state => recordingId => state.commentedIds.includes(recordingId),
  },

  mutations: {
    SET_OPEN(state, value) { state.isOpen = value; },
    SET_MODE(state, value) { state.mode = value; },
    SET_RECORDING_ID(state, value) { state.recordingId = value; },
    SET_MY_COMMENT(state, value) { state.myComment = value; },
    SET_THREAD(state, value) { state.thread = value; },
    SET_LOADING(state, value) { state.loading = value; },
    SET_SAVING(state, value) { state.saving = value; },
    SET_COMMENTED_IDS(state, ids) { state.commentedIds = Array.isArray(ids) ? ids : []; },
    ADD_COMMENTED_ID(state, id) {
      if (!state.commentedIds.includes(id)) {
        // reassign for Vue 2 reactivity
        state.commentedIds = [...state.commentedIds, id];
      }
    },
    REMOVE_COMMENTED_ID(state, id) {
      state.commentedIds = state.commentedIds.filter(x => x !== id);
    },
  },

  actions: {
    /**
     * Load which recordings the user has already commented on (filled vs outline icon).
     * Non-fatal: on failure the icons simply stay outline.
     */
    async fetchMyCommentedRecordings({ commit }) {
      try {
        const ids = await ApiService.getMyCommentedRecordings();
        commit('SET_COMMENTED_IDS', ids);
      } catch (err) {
        // ignore — icon state is cosmetic
      }
    },

    /**
     * Open the editor for the user's own comment on a track (poll voting mode).
     */
    async openEdit({ commit, dispatch }, recordingId) {
      commit('SET_RECORDING_ID', recordingId);
      commit('SET_MODE', 'edit');
      commit('SET_MY_COMMENT', null);
      commit('SET_OPEN', true);
      commit('SET_LOADING', true);
      try {
        const comment = await ApiService.getMyComment(recordingId);
        commit('SET_MY_COMMENT', comment);
      } catch (err) {
        dispatch('ui/showSnackbar', "Couldn't load your comment", { root: true });
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Open the anonymous comment thread for a track (poll results mode, read-only).
     */
    async openThread({ commit, dispatch }, recordingId) {
      commit('SET_RECORDING_ID', recordingId);
      commit('SET_MODE', 'thread');
      commit('SET_THREAD', []);
      commit('SET_OPEN', true);
      commit('SET_LOADING', true);
      try {
        const thread = await ApiService.getTrackThread(recordingId);
        commit('SET_THREAD', thread);
      } catch (err) {
        dispatch('ui/showSnackbar', "Couldn't load comments", { root: true });
      } finally {
        commit('SET_LOADING', false);
      }
    },

    close({ commit }) {
      commit('SET_OPEN', false);
    },

    /**
     * Persist the user's comment for the currently-open track. A blank body deletes it.
     * Updates the icon state and closes the dialog on success.
     */
    async saveMyComment({ state, commit, dispatch }, body) {
      const recordingId = state.recordingId;
      if (recordingId == null) return;
      commit('SET_SAVING', true);
      try {
        const result = await ApiService.setMyComment(recordingId, body);
        if (result && result.deleted) {
          commit('REMOVE_COMMENTED_ID', recordingId);
        } else {
          commit('ADD_COMMENTED_ID', recordingId);
        }
        commit('SET_OPEN', false);
      } catch (err) {
        dispatch('ui/showSnackbar', "Couldn't save your comment — please try again", { root: true });
      } finally {
        commit('SET_SAVING', false);
      }
    },
  },
};
